import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Note: This uses framer-motion for animations. You would need to install it:
// npm install framer-motion

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  // In a real implementation, you would import these images from your assets
  // const teamMembers = [
  //   {
  //     name: 'Sarah Johnson',
  //     position: 'Founder & CEO',
  //     image:
  //       'https://img.freepik.com/free-photo/happy-ambitious-hr-manager-choosing-you_1262-19137.jpg?t=st=1745906361~exp=1745909961~hmac=f85c6d4df034401174a1acf67177d24c5eec70a0409f4875eff69888a1dc0133&w=996',
  //     bio: 'With over 15 years in the organic flour industry, Sarah founded FlourMart with a vision to bring premium quality flour products to home bakers.',
  //   },
  //   {
  //     name: 'Michael Chen',
  //     position: 'Head of Product Quality',
  //     image:
  //       'https://img.freepik.com/premium-photo/man-suit-is-giving-thumbs-up-he-is-smiling-he-is-happy_137441-19742.jpg?w=996',
  //     bio: 'Michael ensures all our flour products meet the highest standards. His background in food science has been instrumental in developing our testing processes.',
  //   },
  //   {
  //     name: 'Priya Patel',
  //     position: 'Sustainability Director',
  //     image:
  //       'https://img.freepik.com/premium-photo/implementing-product-reviews-ratings_1104763-19429.jpg?w=740',
  //     bio: 'Priya leads our initiatives for sustainable farming practices and ensures our packaging is environmentally friendly.',
  //   },
  // ];

  return (
    <div className='bg-gray-50 min-h-screen pt-24 pb-16 '>
      {/* Hero Section */}
      <motion.section
        className='relative bg-green-600 text-white mt-8 py-16 md:py-24 overflow-hidden'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          backgroundImage:
            "url('https://img.freepik.com/free-photo/stashed-flour-used-cooking_23-2149517214.jpg?t=st=1745906171~exp=1745909771~hmac=583d3f5576684161ff0857964b138d95e5f31831559463fcea2b9b59d8e5af7b&w=740')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className='absolute inset-0 overflow-hidden'>
          <div className='absolute inset-0 bg-black opacity-30'></div>
          {/* Background pattern */}
          <div className='absolute inset-0 opacity-10'>
            <div className='grid grid-cols-10 h-full w-full'>
              {[...Array(100)].map((_, i) => (
                <div key={i} className='border border-white'></div>
              ))}
            </div>
          </div>
        </div>

        <div className='container mx-auto px-4 relative z-10'>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className='max-w-3xl mx-auto text-center'
          >
            <h1 className='text-4xl md:text-5xl lg:text-6xl font-bold mb-6'>
              Our Flour Story
            </h1>
            <p className='text-lg md:text-xl opacity-90 mb-8'>
              Since 2010, we've been on a mission to bring the finest
              quality flour products to your kitchen.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Our Journey */}
      <section className='py-16 md:py-24'>
        <div className='container mx-auto px-4'>
          <motion.div
            className='flex flex-col md:flex-row items-center gap-8 md:gap-16'
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerChildren}
          >
            <motion.div
              className='w-full md:w-1/2 order-2 md:order-1'
              variants={fadeIn}
            >
              <h2 className='text-3xl md:text-4xl font-bold text-gray-800 mb-6'>
                Our Stone-Ground Legacy
              </h2>
              <p className='text-gray-600 mb-4'>
              Pralhad Flours journey began with a simple yet powerful
                belief — that traditional stone grinding produces
                flour of unmatched quality. Our roots lie in age-old
                milling methods passed down through generations, where
                every grain is ground slowly and naturally between
                heavy stones.
              </p>
              <p className='text-gray-600 mb-4'>
                Unlike modern high-speed steel rollers, stone grinding
                retains the germ, bran, and essential nutrients of the
                grain. This process not only preserves natural flavor
                and texture but also honors the wholesome goodness of
                ancient grains.
              </p>
              <p className='text-gray-600'>
                Today, we proudly combine tradition with
                sustainability by sourcing from local farmers and
                maintaining the purity of stone-ground flour. Every
                batch is crafted with care — because we believe great
                health starts with great flour.
              </p>
            </motion.div>

            <motion.div
              className='w-full md:w-1/2 order-1 md:order-2'
              variants={fadeIn}
            >
              <div className='rounded-lg overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-300'>
                <img
                  src='https://img.freepik.com/free-photo/side-view-khinkali-with-flour-human-hand_176474-3125.jpg?t=st=1745906266~exp=1745909866~hmac=569c6fcece16e05db35a0e8fa8e753fc1ffdf2b96dc63416127859d111ea863f&w=740'
                  alt='Our flour mill'
                  className='w-full h-auto object-cover'
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Our Values */}
      <motion.section
        className='py-16 md:py-24 bg-white'
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerChildren}
      >
        <div className='container mx-auto px-4'>
          <motion.h2
            className='text-3xl md:text-4xl font-bold text-center text-gray-800 mb-16'
            variants={fadeIn}
          >
            Our Values
          </motion.h2>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            <motion.div
              className='bg-gray-50 rounded-lg p-8 shadow-md hover:shadow-xl transition-shadow duration-300'
              variants={fadeIn}
            >
              <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-8 w-8 text-green-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 13l4 4L19 7'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-gray-800 mb-4 text-center'>
                Quality
              </h3>
              <p className='text-gray-600 text-center'>
                We never compromise on quality. From seed selection to
                packaging, every step meets our rigorous standards.
              </p>
            </motion.div>

            <motion.div
              className='bg-gray-50 rounded-lg p-8 shadow-md hover:shadow-xl transition-shadow duration-300'
              variants={fadeIn}
            >
              <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-8 w-8 text-green-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-gray-800 mb-4 text-center'>
                Sustainability
              </h3>
              <p className='text-gray-600 text-center'>
                We're committed to eco-friendly practices in farming,
                production, and packaging to protect our planet for
                future generations.
              </p>
            </motion.div>

            <motion.div
              className='bg-gray-50 rounded-lg p-8 shadow-md hover:shadow-xl transition-shadow duration-300'
              variants={fadeIn}
            >
              <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-8 w-8 text-green-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-gray-800 mb-4 text-center'>
                Community
              </h3>
              <p className='text-gray-600 text-center'>
                We support local farmers and communities, believing
                that strong relationships lead to better products and
                a better world.
              </p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Testimonials */}
      {/* <motion.section
        className='py-16 md:py-24 bg-gray-50'
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerChildren}
      >
        <div className='container mx-auto px-4'>
          <motion.div className='text-center mb-16' variants={fadeIn}>
            <h2 className='text-3xl md:text-4xl font-bold text-gray-800 mb-4'>
              What Our Customers Say
            </h2>
            <p className='text-gray-600 max-w-2xl mx-auto'>
              Don't just take our word for it. Here's what some of our
              happy customers have to say about FlourMart products.
            </p>
          </motion.div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {[
              {
                name: 'Emma Thompson',
                location: 'Home Baker, New York',
                quote:
                  "I've tried many flour brands, but FlourMart's consistency and quality is unmatched. My bread has never been better!",
              },
              {
                name: 'David Rodriguez',
                location: 'Pastry Chef, Chicago',
                quote:
                  "As a professional, I need reliable ingredients. FlourMart's pastry flour gives me perfect results every single time.",
              },
              {
                name: 'Lisa Chen',
                location: 'Food Blogger, Los Angeles',
                quote:
                  "Since switching to FlourMart's organic whole wheat, my recipes have reached a new level. My followers have noticed the difference!",
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className='bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300'
                variants={fadeIn}
              >
                <div className='flex items-center mb-4'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-8 w-8 text-green-500'
                    fill='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path d='M14.017 18L14.017 10.609C14.017 4.905 17.748 1.039 23 0L23.995 2.151C21.563 3.068 20 5.789 20 8H24V18H14.017ZM0 18V10.609C0 4.905 3.748 1.038 9 0L9.996 2.151C7.563 3.068 6 5.789 6 8H9.983L9.983 18L0 18Z' />
                  </svg>
                </div>
                <p className='text-gray-600 mb-6 italic'>
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className='font-medium text-gray-800'>
                    {testimonial.name}
                  </p>
                  <p className='text-green-600 text-sm'>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section> */}

      {/* Contact CTA */}
      <motion.section
        className='py-16 md:py-24 bg-green-600 text-white'
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
      >
        <div className='container mx-auto px-4'>
          <motion.div
            className='max-w-3xl mx-auto text-center'
            variants={fadeIn}
          >
            <h2 className='text-3xl md:text-4xl font-bold mb-6'>
              Want to learn more?
            </h2>
            <p className='text-lg mb-8 opacity-90'>
              Have questions about our products or want to join our
              community of flour enthusiasts? We'd love to hear from
              you!
            </p>
            <div className='flex flex-col sm:flex-row justify-center gap-4'>
              <button
                onClick={() => navigate('/contact')}
                className='bg-white text-green-600 hover:bg-gray-100 py-3 px-8 rounded-full font-medium transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50'
              >
                Contact Us
              </button>
              <button
                onClick={() => navigate('/products')}
                className='border-2 border-white text-white hover:bg-white hover:text-green-600 py-3 px-8 rounded-full font-medium transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50'
              >
                Visit Our Store
              </button>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default AboutUs;
